/**
 * Resource management — planning resource CRUD with inline editable table.
 * Route: /schedule/resources
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  Loader,
  Modal,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconBuildingFactory2,
  IconDeviceFloppy,
  IconPlus,
  IconRefresh,
  IconTrash,
} from '@tabler/icons-react';
import {
  createPlanningResource,
  deletePlanningResource,
  fetchPlanningResources,
  PLANNING_RESOURCE_TYPES,
  toUpdatePlanningResourceRequest,
  updatePlanningResource,
  type PlanningResource,
  type PlanningResourceType,
} from '../../api/planningResourceApis';
import { fetchProjectCalendars } from '../../api/schedulingApis';
import { getProjectId } from '../../utils/cookies';
import { getProjectDisplayName } from '../../utils/projectDisplay';
import { ResourceBulkToolbar, type BulkApplyPatch } from './components/ResourceBulkToolbar';
import { RESOURCE_TABLE_COL as COL, ResourceTableRow } from './components/ResourceTableRow';
import { RateLabel } from './components/RateLabel';
import './resource-management.css';

type ResourceDraft = Partial<
  Pick<
    PlanningResource,
    | 'name'
    | 'type'
    | 'unitOfMeasure'
    | 'maxUnits'
    | 'maxUnitsPerDay'
    | 'standardRate'
    | 'overtimeRate'
    | 'costPerUse'
    | 'initials'
    | 'email'
    | 'calendarId'
    | 'notes'
  >
>;

type AddFormState = {
  name: string;
  type: PlanningResourceType;
  unitOfMeasure: string;
  maxUnits: number;
  maxUnitsPerDay: number;
  standardRate: number;
  overtimeRate: number;
  costPerUse: number;
  initials: string;
  email: string;
  calendarId: string;
  notes: string;
};

const TYPE_OPTIONS = PLANNING_RESOURCE_TYPES.map((value) => ({
  value,
  label: value.charAt(0) + value.slice(1).toLowerCase(),
}));

const EMPTY_ADD_FORM: AddFormState = {
  name: '',
  type: 'WORK',
  unitOfMeasure: 'h',
  maxUnits: 0,
  maxUnitsPerDay: 1,
  standardRate: 0,
  overtimeRate: 0,
  costPerUse: 0,
  initials: '',
  email: '',
  calendarId: '',
  notes: '',
};

function mergeRow(row: PlanningResource, draft?: ResourceDraft): PlanningResource {
  if (!draft) return row;
  return { ...row, ...draft };
}

function isDraftDirty(draft: ResourceDraft | undefined): boolean {
  return draft != null && Object.keys(draft).length > 0;
}

export default function ResourceManagementPage() {
  const projectId = getProjectId() || '';
  const projectDisplayName = getProjectDisplayName();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<PlanningResource[]>([]);
  const [drafts, setDrafts] = useState<Record<string, ResourceDraft>>({});
  const [calendarOptions, setCalendarOptions] = useState<
    Array<{ value: string; label: string; isDefault: boolean }>
  >([]);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddFormState>(EMPTY_ADD_FORM);
  const [adding, setAdding] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<PlanningResource | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const dirtyIds = useMemo(
    () => Object.keys(drafts).filter((id) => isDraftDirty(drafts[id])),
    [drafts],
  );
  const dirtyCount = dirtyIds.length;
  const selectedCount = selectedIds.size;
  const showBulkToolbar = selectedCount >= 2;

  const loadPage = useCallback(
    async (options?: { force?: boolean }) => {
      if (!projectId) return;
      setLoading(true);
      try {
        const [resources, calendars] = await Promise.all([
          fetchPlanningResources(projectId, { silent: true }),
          fetchProjectCalendars(projectId),
        ]);
        setRows(resources);
        if (options?.force) {
          setDrafts({});
          setSelectedIds(new Set());
        } else {
          setSelectedIds((prev) => {
            const next = new Set([...prev].filter((id) => resources.some((r) => r.id === id)));
            return next.size === prev.size ? prev : next;
          });
        }

        const optionsList = calendars
          .map((c) => ({
            value: String(c.id ?? '').trim(),
            label: String(c.name || 'Unnamed').trim() || 'Unnamed',
            isDefault: Boolean(c.isDefault),
          }))
          .filter((opt) => opt.value.length > 0);
        const seen = new Set<string>();
        const deduped = optionsList.filter((opt) => {
          if (seen.has(opt.value)) return false;
          seen.add(opt.value);
          return true;
        });
        setCalendarOptions(deduped);
      } catch (err) {
        console.error('[Resources] Failed to load:', err);
        notifications.show({
          title: 'Could not load resources',
          message: 'Failed to fetch planning resources.',
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    },
    [projectId],
  );

  useEffect(() => {
    void loadPage({ force: true });
  }, [loadPage]);

  const defaultCalendarId = useMemo(() => {
    const preferred = calendarOptions.find((c) => c.isDefault);
    return preferred?.value || calendarOptions[0]?.value || '';
  }, [calendarOptions]);

  const patchDraft = useCallback(
    (id: string, patch: ResourceDraft) => {
      setDrafts((prev) => {
        const base = rows.find((r) => r.id === id);
        if (!base) return prev;
        const current = { ...(prev[id] ?? {}) };
        for (const [key, value] of Object.entries(patch) as Array<
          [keyof ResourceDraft, ResourceDraft[keyof ResourceDraft]]
        >) {
          const original = base[key];
          if (value === original || (value == null && original == null)) {
            delete current[key];
          } else if (
            typeof value === 'number' &&
            typeof original === 'number' &&
            value === original
          ) {
            delete current[key];
          } else {
            (current as Record<string, unknown>)[key] = value;
          }
        }
        if (Object.keys(current).length === 0) {
          const next = { ...prev };
          delete next[id];
          return next;
        }
        return { ...prev, [id]: current };
      });
    },
    [rows],
  );

  const handleCancel = () => {
    setDrafts({});
  };

  const handleSave = async () => {
    if (!projectId || dirtyCount === 0 || saving) return;
    setSaving(true);
    try {
      const results = await Promise.all(
        dirtyIds.map(async (id) => {
          const base = rows.find((r) => r.id === id);
          if (!base) return { id, ok: false as const };
          try {
            const merged = mergeRow(base, drafts[id]);
            await updatePlanningResource(id, toUpdatePlanningResourceRequest(merged), {
              silent: true,
            });
            return { id, ok: true as const };
          } catch {
            return { id, ok: false as const };
          }
        }),
      );

      const failed = results.filter((r) => !r.ok).length;
      const succeeded = results.length - failed;

      if (failed === 0) {
        notifications.show({
          title: 'Resources saved',
          message:
            succeeded === 1
              ? '1 resource updated.'
              : `${succeeded} resources updated.`,
          color: 'teal',
        });
        setDrafts({});
      } else {
        const nextDrafts: Record<string, ResourceDraft> = {};
        for (const result of results) {
          if (!result.ok && drafts[result.id]) {
            nextDrafts[result.id] = drafts[result.id];
          }
        }
        setDrafts(nextDrafts);
        notifications.show({
          title: 'Partial save',
          message: `${succeeded} saved, ${failed} failed. Unsaved rows remain edited.`,
          color: 'orange',
        });
      }
      await loadPage();
    } finally {
      setSaving(false);
    }
  };

  const openAddModal = () => {
    setAddForm({
      ...EMPTY_ADD_FORM,
      calendarId: defaultCalendarId,
    });
    setAddOpen(true);
  };

  const canSubmitAdd =
    addForm.name.trim().length > 0 &&
    Boolean(addForm.type) &&
    addForm.unitOfMeasure.trim().length > 0;

  const handleAdd = async () => {
    if (!projectId || adding || !canSubmitAdd) return;
    const name = addForm.name.trim();
    setAdding(true);
    try {
      await createPlanningResource(
        {
          projectId,
          name,
          type: addForm.type,
          unitOfMeasure: addForm.unitOfMeasure.trim(),
          maxUnits: addForm.maxUnits,
          maxUnitsPerDay: addForm.maxUnitsPerDay,
          standardRate: addForm.standardRate,
          overtimeRate: addForm.overtimeRate,
          costPerUse: addForm.costPerUse,
          initials: addForm.initials.trim(),
          email: addForm.email.trim() || null,
          calendarId: addForm.calendarId.trim(),
          notes: addForm.notes.trim() || null,
        },
        { silent: false },
      );
      setAddOpen(false);
      setAddForm(EMPTY_ADD_FORM);
      await loadPage({ force: true });
    } catch {
      // notification already shown by API helper
    } finally {
      setAdding(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await deletePlanningResource(deleteTarget.id, { silent: false });
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[deleteTarget.id];
        return next;
      });
      setSelectedIds((prev) => {
        if (!prev.has(deleteTarget.id)) return prev;
        const next = new Set(prev);
        next.delete(deleteTarget.id);
        return next;
      });
      setDeleteTarget(null);
      await loadPage();
    } catch {
      // notification already shown
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (rows.length === 0) return prev;
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  }, [rows]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const bulkToolbarKeyRef = useRef(0);
  const prevShowBulkRef = useRef(false);
  if (showBulkToolbar && !prevShowBulkRef.current) {
    bulkToolbarKeyRef.current += 1;
  }
  prevShowBulkRef.current = showBulkToolbar;

  const handleBulkApply = useCallback(
    (patch: BulkApplyPatch) => {
      if (selectedIds.size < 2) return;
      if (Object.keys(patch).length === 0) {
        notifications.show({
          title: 'Nothing to apply',
          message: 'Fill at least one field to update selected rows.',
          color: 'orange',
        });
        return;
      }

      setDrafts((prev) => {
        const next = { ...prev };
        for (const id of selectedIds) {
          const base = rows.find((r) => r.id === id);
          if (!base) continue;
          const current = { ...(next[id] ?? {}) };
          for (const [key, value] of Object.entries(patch) as Array<
            [keyof ResourceDraft, ResourceDraft[keyof ResourceDraft]]
          >) {
            const original = base[key];
            if (value === original || (value == null && original == null)) {
              delete current[key];
            } else {
              (current as Record<string, unknown>)[key] = value;
            }
          }
          if (Object.keys(current).length === 0) delete next[id];
          else next[id] = current;
        }
        return next;
      });

      notifications.show({
        title: 'Applied to selection',
        message: `Updated ${selectedIds.size} rows locally. Save to persist.`,
        color: 'teal',
      });
    },
    [rows, selectedIds],
  );

  const handleBulkDelete = async () => {
    if (selectedIds.size < 2 || deleting) return;
    setDeleting(true);
    try {
      const ids = [...selectedIds];
      const results = await Promise.all(
        ids.map(async (id) => {
          try {
            await deletePlanningResource(id, { silent: true });
            return { id, ok: true as const };
          } catch {
            return { id, ok: false as const };
          }
        }),
      );
      const failed = results.filter((r) => !r.ok).length;
      const succeeded = results.length - failed;

      setDrafts((prev) => {
        const next = { ...prev };
        for (const r of results) {
          if (r.ok) delete next[r.id];
        }
        return next;
      });

      if (failed === 0) {
        notifications.show({
          title: 'Resources deleted',
          message: `${succeeded} resources removed.`,
          color: 'teal',
        });
        setSelectedIds(new Set());
      } else {
        setSelectedIds(new Set(results.filter((r) => !r.ok).map((r) => r.id)));
        notifications.show({
          title: 'Partial delete',
          message: `${succeeded} deleted, ${failed} failed.`,
          color: 'orange',
        });
      }
      setBulkDeleteOpen(false);
      await loadPage();
    } finally {
      setDeleting(false);
    }
  };

  if (!projectId) {
    return (
      <Box p="xl">
        <Text c="red">No project selected.</Text>
      </Box>
    );
  }

  const displayRows = rows.map((row) => mergeRow(row, drafts[row.id]));

  return (
    <Box
      className="rm-page"
      style={{
        height: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'var(--app-shell-bg, var(--mantine-color-body))',
      }}
    >
      <Box className="rm-page__header">
        <Group justify="space-between" align="center" wrap="wrap" gap="md">
          <Group gap={12} align="center" wrap="nowrap">
            <Box className="rm-page__icon" aria-hidden>
              <IconBuildingFactory2 size={18} stroke={1.6} />
            </Box>
            <Stack gap={2}>
              <Text fw={600} style={{ fontSize: 18, letterSpacing: '-0.18px', lineHeight: 1.3 }}>
                Resource management
              </Text>
              <Text size="sm" c="dimmed">
                {projectDisplayName
                  ? `Planning resources for ${projectDisplayName}`
                  : 'Create and edit project planning resources'}
              </Text>
            </Stack>
          </Group>
          <Group gap="xs" align="center" wrap="wrap">
            <span className="rm-chip rm-chip--muted">
              {displayRows.length}{' '}
              {displayRows.length === 1 ? 'resource' : 'resources'}
            </span>
            {dirtyCount > 0 ? (
              <span className="rm-chip rm-chip--dirty">
                {dirtyCount} unsaved {dirtyCount === 1 ? 'edit' : 'edits'}
              </span>
            ) : (
              <span className="rm-chip rm-chip--muted">All changes saved</span>
            )}
            <Tooltip label="Refresh list">
              <ActionIcon
                variant="default"
                size={32}
                radius="md"
                aria-label="Refresh"
                onClick={() => void loadPage({ force: true })}
                loading={loading}
                disabled={saving}
              >
                <IconRefresh size={15} />
              </ActionIcon>
            </Tooltip>
            <Button
              size="xs"
              radius="md"
              color="brand"
              leftSection={<IconPlus size={14} />}
              onClick={openAddModal}
              disabled={saving}
            >
              Add resource
            </Button>
          </Group>
        </Group>
      </Box>

      <Box className="rm-page__body">
        {loading && rows.length === 0 ? (
          <Box className="rm-panel">
            <Group justify="center" align="center" style={{ flex: 1, minHeight: 220 }}>
              <Loader size="sm" color="brand" />
              <Text size="sm" c="dimmed">
                Loading resources…
              </Text>
            </Group>
          </Box>
        ) : displayRows.length === 0 ? (
          <Box className="rm-panel">
            <Box className="rm-empty">
              <Box className="rm-empty__icon" aria-hidden>
                <IconBuildingFactory2 size={26} stroke={1.5} />
              </Box>
              <Text fw={600} size="sm">
                No resources yet
              </Text>
              <Text size="sm" c="dimmed" maw={340}>
                Add work crews, materials, or cost items to assign them across the schedule.
              </Text>
              <Button
                mt={6}
                size="xs"
                radius="md"
                color="brand"
                leftSection={<IconPlus size={14} />}
                onClick={openAddModal}
              >
                Add resource
              </Button>
            </Box>
          </Box>
        ) : (
          <Box className="rm-panel">
            {showBulkToolbar ? (
              <ResourceBulkToolbar
                key={bulkToolbarKeyRef.current}
                selectedCount={selectedCount}
                calendarOptions={calendarOptions}
                disabled={saving || deleting}
                onClearSelection={clearSelection}
                onApply={handleBulkApply}
                onDelete={() => setBulkDeleteOpen(true)}
              />
            ) : null}

            <Table.ScrollContainer minWidth={1280} className="rm-table-scroll">
              <Table
                className="rm-table"
                horizontalSpacing={0}
                verticalSpacing={0}
                withRowBorders={false}
              >
                <Table.Thead>
                  <Table.Tr className="rm-table__group">
                    <Table.Th
                      className="rm-col--sticky rm-col--check"
                      style={{ width: COL.check, minWidth: COL.check }}
                    />
                    <Table.Th
                      className="rm-col--sticky rm-col--name"
                      style={{ minWidth: COL.name, width: COL.name }}
                    >
                      Resource
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.type, width: COL.type }} />
                    <Table.Th style={{ minWidth: COL.unit, width: COL.unit }} />
                    <Table.Th colSpan={2}>Capacity</Table.Th>
                    <Table.Th colSpan={3}>
                      <RateLabel>Rates</RateLabel>
                    </Table.Th>
                    <Table.Th colSpan={4}>Details</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                  <Table.Tr className="rm-table__cols">
                    <Table.Th
                      className="rm-col--sticky rm-col--check"
                      style={{ width: COL.check, minWidth: COL.check }}
                    >
                      <Checkbox
                        size="xs"
                        aria-label="Select all resources"
                        checked={rows.length > 0 && selectedIds.size === rows.length}
                        indeterminate={
                          selectedIds.size > 0 && selectedIds.size < rows.length
                        }
                        onChange={toggleSelectAll}
                      />
                    </Table.Th>
                    <Table.Th
                      className="rm-col--sticky rm-col--name"
                      style={{ minWidth: COL.name, width: COL.name }}
                    >
                      Name
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.type, width: COL.type }}>
                      Type
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.unit, width: COL.unit }}>
                      Unit
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.maxUnits, width: COL.maxUnits }}>
                      Max units
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.maxDay, width: COL.maxDay }}>
                      Max / day
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.std, width: COL.std }}>
                      <RateLabel>Std rate</RateLabel>
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.ot, width: COL.ot }}>
                      <RateLabel>OT rate</RateLabel>
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.cost, width: COL.cost }}>
                      <RateLabel>Cost / use</RateLabel>
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.initials, width: COL.initials }}>
                      Initials
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.calendar, width: COL.calendar }}>
                      Calendar
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.notes, width: COL.notes }}>
                      Notes
                    </Table.Th>
                    <Table.Th style={{ minWidth: COL.email, width: COL.email }}>
                      Email
                    </Table.Th>
                    <Table.Th style={{ width: COL.actions }} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {displayRows.map((row) => (
                    <ResourceTableRow
                      key={row.id}
                      row={row}
                      dirty={isDraftDirty(drafts[row.id])}
                      selected={selectedIds.has(row.id)}
                      calendarOptions={calendarOptions}
                      saving={saving || deleting}
                      onToggleSelect={toggleSelect}
                      onPatch={patchDraft}
                      onDelete={setDeleteTarget}
                    />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            {dirtyCount > 0 ? (
              <Box className="rm-dirty-bar">
                <Text size="sm" fw={550} c="orange.8">
                  {dirtyCount} {dirtyCount === 1 ? 'row has' : 'rows have'} unsaved
                  changes
                </Text>
                <Group gap="xs">
                  <Button
                    variant="default"
                    size="xs"
                    radius="md"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Discard
                  </Button>
                  <Button
                    size="xs"
                    radius="md"
                    color="brand"
                    leftSection={<IconDeviceFloppy size={14} />}
                    onClick={() => void handleSave()}
                    loading={saving}
                  >
                    Save
                  </Button>
                </Group>
              </Box>
            ) : null}
          </Box>
        )}
      </Box>

      <Modal
        opened={addOpen}
        onClose={() => !adding && setAddOpen(false)}
        title="Add resource"
        size="lg"
        centered
        radius="md"
        classNames={{ body: 'rm-add-modal' }}
      >
        <Stack gap="sm">
          <TextInput
            label="Name"
            required
            value={addForm.name}
            onChange={(e) =>
              setAddForm((prev) => ({ ...prev, name: e.currentTarget.value }))
            }
            placeholder="e.g. Mason crew"
          />
          <Group grow>
            <Select
              label="Type"
              required
              data={TYPE_OPTIONS}
              value={addForm.type}
              allowDeselect={false}
              onChange={(value) =>
                setAddForm((prev) => ({
                  ...prev,
                  type: (value as PlanningResourceType) || 'WORK',
                }))
              }
            />
            <TextInput
              label="Unit of measure"
              required
              value={addForm.unitOfMeasure}
              onChange={(e) =>
                setAddForm((prev) => ({
                  ...prev,
                  unitOfMeasure: e.currentTarget.value,
                }))
              }
            />
          </Group>
          <Select
            className="rm-form-select-clearable"
            label="Calendar"
            data={calendarOptions}
            value={addForm.calendarId || null}
            placeholder="Select calendar"
            searchable
            clearable
            onChange={(value) =>
              setAddForm((prev) => ({ ...prev, calendarId: value || '' }))
            }
          />
          <Group grow>
            <NumberInput
              label="Max units"
              value={addForm.maxUnits}
              hideControls
              decimalScale={2}
              onChange={(value) =>
                setAddForm((prev) => ({
                  ...prev,
                  maxUnits: typeof value === 'number' ? value : 0,
                }))
              }
            />
            <NumberInput
              label="Max units / day"
              value={addForm.maxUnitsPerDay}
              hideControls
              decimalScale={2}
              onChange={(value) =>
                setAddForm((prev) => ({
                  ...prev,
                  maxUnitsPerDay: typeof value === 'number' ? value : 0,
                }))
              }
            />
          </Group>
          <Group grow>
            <NumberInput
              label={<RateLabel>Standard rate</RateLabel>}
              value={addForm.standardRate}
              hideControls
              decimalScale={2}
              onChange={(value) =>
                setAddForm((prev) => ({
                  ...prev,
                  standardRate: typeof value === 'number' ? value : 0,
                }))
              }
            />
            <NumberInput
              label={<RateLabel>Overtime rate</RateLabel>}
              value={addForm.overtimeRate}
              hideControls
              decimalScale={2}
              onChange={(value) =>
                setAddForm((prev) => ({
                  ...prev,
                  overtimeRate: typeof value === 'number' ? value : 0,
                }))
              }
            />
            <NumberInput
              label={<RateLabel>Cost per use</RateLabel>}
              value={addForm.costPerUse}
              hideControls
              decimalScale={2}
              onChange={(value) =>
                setAddForm((prev) => ({
                  ...prev,
                  costPerUse: typeof value === 'number' ? value : 0,
                }))
              }
            />
          </Group>
          <Group grow>
            <TextInput
              label="Initials"
              value={addForm.initials}
              onChange={(e) =>
                setAddForm((prev) => ({ ...prev, initials: e.currentTarget.value }))
              }
            />
          </Group>
          <TextInput
            label="Notes"
            value={addForm.notes}
            onChange={(e) =>
              setAddForm((prev) => ({ ...prev, notes: e.currentTarget.value }))
            }
          />
          <TextInput
            label="Email"
            value={addForm.email}
            onChange={(e) =>
              setAddForm((prev) => ({ ...prev, email: e.currentTarget.value }))
            }
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={() => setAddOpen(false)} disabled={adding}>
              Cancel
            </Button>
            <Button
              color="brand"
              onClick={() => void handleAdd()}
              loading={adding}
              disabled={!canSubmitAdd}
            >
              Add resource
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteTarget != null}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete resource"
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Delete{' '}
            <Text span fw={600}>
              {deleteTarget?.name || 'this resource'}
            </Text>
            ? This cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setDeleteTarget(null)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => void handleConfirmDelete()}
              loading={deleting}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={bulkDeleteOpen}
        onClose={() => !deleting && setBulkDeleteOpen(false)}
        title="Delete selected resources"
        centered
        radius="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Delete{' '}
            <Text span fw={600}>
              {selectedCount} selected resources
            </Text>
            ? This cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="default"
              onClick={() => setBulkDeleteOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              color="red"
              leftSection={<IconTrash size={14} />}
              onClick={() => void handleBulkDelete()}
              loading={deleting}
            >
              Delete {selectedCount}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
}
